import {TargetBlank} from '@wandb/common/util/links';
import React, {useEffect, useState} from 'react';
import {toast} from 'react-toastify';
import {Loader} from 'semantic-ui-react';

import {
  RunQueueAccessType,
  TriggeredActionType,
  TriggerScopeType,
  useCreateDefaultResourceConfigMutation,
  useCreateFilterTriggerMutation,
  useCreateRunQueueMutation,
  useUpsertModelMutation,
} from '../../../../generated/graphql';
import {DEFAULT_LAUNCH_PROJECT_NAME} from '../../../../pages/EntityLaunchPage';
import {propagateErrorsContext} from '../../../../util/errors';
import {toMongo} from '../../../../util/filters';
import {queue as queueUrl} from '../../../../util/urls';
import {Button} from '../../../Button';
import {IconInfo} from '../../../Icon';
import {JobDetails} from '../../JobDetail/utilTypes';
import {JobOption} from '../../SelectJob';
import {getDefaultQueue, QueueOption, QueueOptions} from '../../SelectQueue';
import {TriggerRootFilter} from '../TriggerEventConfiguration';
import {EventTriggeringConditionType, Trigger} from '../TriggerEventTypes';
import {getDefaultTriggerDescription, OptionType} from '../utils';
import * as S from './CreateTriggerDrawerLoaded.styles';
import {DrawerPageEventConfiguration} from './DrawerPageEventConfiguration';
import {DrawerPageJobConfiguration} from './DrawerPageJobConfiguration';
import {DrawerPageSummary} from './DrawerPageSummary';
import {EmptyTriggerDrawer} from './EmptyTriggerDrawer';

const EMPTY_TRIGGER_FILTERS: TriggerRootFilter = {
  op: 'OR',
  filters: [
    {
      op: 'AND',
      filters: [],
    },
  ],
};

type CreateTriggerDrawerLoadedProps = {
  entityName: string;
  projectName: string;
  projectID: string;
  artifactCollectionID?: string;
  allProjects: string[];
  queues: QueueOptions;
  jobDetailsMap: Record<string, JobDetails>;
  onClose: () => void;
  refetchProjectTriggers: () => Promise<any>;
  refetchCollectionTriggers?: () => Promise<any>;
  customTriggerOptions?: OptionType[];
  customRunColors: Record<string, string>;
};

export const CreateTriggerDrawerLoaded = ({
  entityName,
  projectName,
  projectID,
  artifactCollectionID,
  allProjects,
  queues,
  jobDetailsMap,
  onClose,
  refetchProjectTriggers,
  refetchCollectionTriggers,
  customTriggerOptions,
  customRunColors,
}: CreateTriggerDrawerLoadedProps) => {
  const [nextButtonDisabled, setNextButtonDisabled] = useState(true);

  // Event configuration state
  const [eventTriggeringConditionType, setEventTriggeringConditionType] =
    useState<EventTriggeringConditionType | undefined>();
  const [filters, setFilters] = useState<TriggerRootFilter>(
    EMPTY_TRIGGER_FILTERS
  );
  const [step, setStep] = useState(1);
  const [trigger, setTrigger] = useState<Trigger | undefined>();
  const [triggerArtifactCollectionName, setTriggerArtifactCollectionName] =
    useState<string | undefined>();
  const [triggerScopeID, setTriggerScopeID] = useState<string | undefined>();
  const [triggerScopeType, setTriggerScopeType] = useState<
    TriggerScopeType | undefined
  >();

  // Job configuration state
  const [queuedJob, setQueuedJob] = useState<JobDetails | undefined>();
  const [queuedJobAlias, setQueuedJobAlias] = useState<string | undefined>();
  const [queuedJobID, setQueuedJobID] = useState<JobOption | null>(null);

  // When user edits the overrides, clear any previous errors
  const [overrides, setOverrides] = useState('');
  const [overridesError, setOverridesError] = useState('');
  const onOverridesChange = (value: string) => {
    setOverrides(value);
    setOverridesError('');
  };

  const defaultQueue = getDefaultQueue(queues);
  const [loading, setLoading] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState<QueueOption | null>(
    defaultQueue
  );
  const [targetProject, setTargetProject] = useState(
    projectName === DEFAULT_LAUNCH_PROJECT_NAME ? '' : projectName
  );

  // Mutations to create new queue
  const [upsertProject] = useUpsertModelMutation({
    context: propagateErrorsContext(),
  });
  const [createDefaultResourceConfig] = useCreateDefaultResourceConfigMutation({
    context: propagateErrorsContext(),
  });
  const [createRunQueue] = useCreateRunQueueMutation({
    context: propagateErrorsContext(),
  });

  useEffect(() => {
    setFilters(EMPTY_TRIGGER_FILTERS);
  }, [trigger]);

  // Trigger configuration state
  const [createTriggerMutation] = useCreateFilterTriggerMutation({
    context: propagateErrorsContext(),
  });
  const [triggerDescription, setTriggerDescription] = useState('');
  const [triggerName, setTriggerName] = useState('');

  const createTrigger = async () => {
    if (
      trigger == null ||
      queuedJob == null ||
      triggerScopeType == null ||
      triggerScopeID == null ||
      eventTriggeringConditionType == null ||
      filters == null ||
      queuedJobAlias == null ||
      !selectedQueue
    ) {
      return;
    }

    setLoading(true);
    let queueName = selectedQueue.queue;
    let queueID = selectedQueue.queueID;
    try {
      if (selectedQueue.queue === '') {
        // Create a starter queue
        // First, ensure we have the necessary project for an entity-scoped queue.
        await upsertProject({
          variables: {
            entityName,
            name: DEFAULT_LAUNCH_PROJECT_NAME,
          },
        });

        const completeConfig = {
          resource_args: {
            'local-container': {},
          },
        };
        const createConfigResponse = await createDefaultResourceConfig({
          variables: {
            entityName,
            resource: 'local-container',
            config: JSON.stringify(completeConfig),
          },
        });

        const defaultResourceConfigID =
          createConfigResponse.data?.createDefaultResourceConfig
            ?.defaultResourceConfigID;

        queueName = 'Starter queue';
        const createQueueResponse = await createRunQueue({
          variables: {
            entityName,
            projectName: DEFAULT_LAUNCH_PROJECT_NAME,
            queueName,
            access: RunQueueAccessType.Project,
            defaultResourceConfigID,
          },
        });
        queueID = createQueueResponse.data?.createRunQueue?.queueID ?? '';
      }
    } catch (error) {
      setLoading(false);
      console.error(error);
      toast.error('Error creating automation: unable to create entity queue.');
      return;
    }
    const triggeredActionConfig = {
      queueJobActionInput: {
        queueID,
        template: JSON.stringify(
          {
            job: `${entityName}/${queuedJob?.projectName}/${queuedJob?.name}:${queuedJobAlias}`,
            entity: entityName,
            project: targetProject,
            overrides: JSON.parse(overrides),
          },
          null,
          2
        ),
      },
    };
    try {
      const createdTrigger = await createTriggerMutation({
        variables: {
          name: triggerName,
          description:
            triggerDescription !== ''
              ? triggerDescription
              : defaultTriggerDescription,
          triggeringEventType: eventTriggeringConditionType,
          scopeType: triggerScopeType,
          scopeID: triggerScopeID,
          eventFilter: JSON.stringify(toMongo(filters)),
          triggeredActionType: TriggeredActionType.QueueJob,
          triggeredActionConfig,
          enabled: true,
        },
      });
      await refetchProjectTriggers();
      await refetchCollectionTriggers?.();
      setLoading(false);
      const newItemId = createdTrigger?.data?.createFilterTrigger?.trigger?.id;
      if (newItemId) {
        toast.success(
          <S.SuccessfulQueue>
            <IconInfo width={48} height={48} />
            <S.SuccessfulQueueMessage>
              Your trigger was successfully added to '{queueName}'.{' '}
              <TargetBlank href={queueUrl(entityName, queueID, 'runs')}>
                View queue
              </TargetBlank>
            </S.SuccessfulQueueMessage>
          </S.SuccessfulQueue>
        );
      }
    } catch (error) {
      setLoading(false);
      console.error(error);
      toast.error(
        'Something went wrong while trying to create this automation'
      );
    }
    onClose();
  };

  // Enable or disable button to go to the next page based on user input
  useEffect(() => {
    if (
      trigger === undefined ||
      triggerScopeID === undefined ||
      triggerScopeID === ''
    ) {
      setNextButtonDisabled(true);
    } else if (step === 2) {
      if (targetProject === '' || !selectedQueue || overridesError !== '') {
        setNextButtonDisabled(true);
      } else {
        setNextButtonDisabled(false);
      }
    } else {
      setNextButtonDisabled(false);
    }
  }, [
    step,
    trigger,
    triggerScopeID,
    selectedQueue,
    targetProject,
    overridesError,
    setNextButtonDisabled,
  ]);

  const defaultTriggerDescription = getDefaultTriggerDescription({
    jobName: queuedJob?.name ?? '',
    jobAlias: queuedJobAlias ?? '',
    eventType: eventTriggeringConditionType,
    triggerScopeType,
    entityName,
    projectName,
    artifactCollectionName: triggerArtifactCollectionName,
    filters,
  });

  if (loading) {
    return <Loader active />;
  }

  if (Object.keys(jobDetailsMap).length === 0) {
    return (
      <S.CreateTriggerDrawer>
        <EmptyTriggerDrawer />
      </S.CreateTriggerDrawer>
    );
  }
  return (
    <S.CreateTriggerDrawer>
      <S.CenteredFlex>
        <S.Header>Create a new automation</S.Header>
        <S.CloseButton>
          <Button onClick={onClose} icon="close" size="large" variant="ghost" />
        </S.CloseButton>
      </S.CenteredFlex>
      <S.Divider></S.Divider>
      <S.CreateTriggerDrawerMain>
        {step === 1 && (
          <DrawerPageEventConfiguration
            entityName={entityName}
            projectName={projectName}
            projectID={projectID}
            artifactCollectionID={artifactCollectionID}
            trigger={trigger}
            triggerScopeType={triggerScopeType}
            triggerScopeID={triggerScopeID ?? ''}
            setTrigger={setTrigger}
            setEventTriggeringConditionType={setEventTriggeringConditionType}
            setTriggerScopeType={setTriggerScopeType}
            setTriggerScopeID={setTriggerScopeID}
            setTriggerArtifactCollectionName={setTriggerArtifactCollectionName}
            filters={filters}
            setFilters={setFilters}
            customTriggerOptions={customTriggerOptions}
          />
        )}
        {step === 2 && (
          <DrawerPageJobConfiguration
            entityName={entityName}
            projectName={projectName}
            trigger={trigger}
            queuedJobID={queuedJobID}
            jobDetailsMap={jobDetailsMap}
            queuedJob={queuedJob}
            queuedJobAlias={queuedJobAlias}
            setQueuedJobID={setQueuedJobID}
            setQueuedJob={setQueuedJob}
            setQueuedJobAlias={setQueuedJobAlias}
            overrides={overrides}
            overridesError={overridesError}
            onOverridesChange={onOverridesChange}
            setOverridesError={setOverridesError}
            customRunColors={customRunColors}
            allProjects={allProjects}
            targetProject={targetProject}
            setTargetProject={setTargetProject}
            selectedQueue={selectedQueue}
            setSelectedQueue={setSelectedQueue}
            queues={queues}
          />
        )}
        {step === 3 && (
          <DrawerPageSummary
            triggerName={triggerName}
            triggerDescription={triggerDescription}
            defaultTriggerDescription={defaultTriggerDescription}
            setTriggerName={setTriggerName}
            setTriggerDescription={setTriggerDescription}
          />
        )}
      </S.CreateTriggerDrawerMain>
      <S.Divider></S.Divider>
      <S.Footer>
        {step === 1 && (
          <S.HelpMessage> You'll select a job in the next step</S.HelpMessage>
        )}
        {step !== 1 && (
          <S.FooterLeftContent>
            <Button
              variant="primary"
              size="large"
              fullWidth={false}
              onClick={() => {
                setStep(step - 1);
              }}>
              Back
            </Button>
          </S.FooterLeftContent>
        )}
        {step !== 3 && (
          <S.FooterRightContent>
            <Button
              variant="primary"
              size="large"
              onClick={() => {
                setStep(step + 1);
              }}
              fullWidth={false}
              disabled={nextButtonDisabled}>
              Next step
            </Button>
          </S.FooterRightContent>
        )}
        {step === 3 && (
          <S.FooterRightContent>
            <Button
              variant="primary"
              size="large"
              onClick={createTrigger}
              disabled={triggerName === '' || triggerName === null}>
              Create automation
            </Button>
          </S.FooterRightContent>
        )}
      </S.Footer>
    </S.CreateTriggerDrawer>
  );
};
